// ================================================================
// src/freeStudy/core.js
// مغز خالص سیستم — همه Pure و قابل تست
// ================================================================

export const SCHEMA_VERSION = 2;

export const TX_TYPES = {
  PURCHASE_VIDEO: 'PURCHASE_VIDEO',
  PURCHASE_BUNDLE: 'PURCHASE_BUNDLE',
  GRANT: 'GRANT',
  REFUND: 'REFUND',
  MIGRATION: 'MIGRATION',
};

// ================================================================
// A) Ownership — مالکیت
// ================================================================
export function isEffectivelyFree(video) {
  if (!video) return false;
  if (video.isFree === true) return true;
  const p = Number(video.price);
  return !Number.isNaN(p) && p <= 0;
}

export function isUnlocked(video, unlockedVideoIds) {
  if (!video) return false;
  if (isEffectivelyFree(video)) return true;
  const set =
    unlockedVideoIds instanceof Set
      ? unlockedVideoIds
      : new Set(unlockedVideoIds || []);
  return set.has(video.id);
}

export function getUnownedVideos(course, unlockedVideoIds) {
  if (!course?.videos) return [];
  const set =
    unlockedVideoIds instanceof Set
      ? unlockedVideoIds
      : new Set(unlockedVideoIds || []);
  return course.videos.filter((v) => !isEffectivelyFree(v) && !set.has(v.id));
}

export function getRemainingCount(course, unlockedVideoIds) {
  return getUnownedVideos(course, unlockedVideoIds).length;
}

export function getOwnedCount(course, unlockedVideoIds) {
  if (!course?.videos) return 0;
  return course.videos.length - getRemainingCount(course, unlockedVideoIds);
}

// ================================================================
// B) Pricing — قیمت‌گذاری
// ================================================================
export function computeVideoPrice(video) {
  if (!video) return 0;
  if (isEffectivelyFree(video)) return 0;
  const p = Number(video.price);
  return Number.isNaN(p) || p < 0 ? 0 : p;
}

/**
 * قیمت بسته‌ی کل دوره برای یک کاربر مشخص
 * = min( قیمت_رسمی_باقی‌مانده , مجموع_قیمت_تک‌تک )
 */
export function computeBundlePrice(course, purchaseState) {
  if (!course?.videos?.length) return 0;

  const fullPrice = Math.max(0, Number(course?.pricing?.fullCoursePrice) || 0);
  const totalPaid = Math.max(0, Number(purchaseState?.totalPaid) || 0);
  const totalRefunded = Math.max(0, Number(purchaseState?.totalRefunded) || 0);

  const unowned = getUnownedVideos(course, purchaseState?.unlockedVideoIds);
  if (unowned.length === 0) return 0;

  const officialRemaining = Math.max(
    0,
    fullPrice - (totalPaid - totalRefunded)
  );

  const sumOfIndividual = unowned.reduce(
    (s, v) => s + computeVideoPrice(v),
    0
  );

  return Math.min(officialRemaining, sumOfIndividual);
}

/**
 * پیشنهاد هوشمند برای کاربری که روی یک ویدیوی قفل کلیک کرده
 */
export function getSmartSuggestion(course, purchaseState, videoId) {
  const video = course?.videos?.find((v) => v.id === videoId);
  if (!video) return null;

  const videoPrice = computeVideoPrice(video);
  const bundlePrice = computeBundlePrice(course, purchaseState);
  const unowned = getUnownedVideos(course, purchaseState?.unlockedVideoIds);

  if (unowned.length <= 1) {
    return { type: 'single', price: videoPrice, saving: 0 };
  }

  const sumIfBoughtIndividually = unowned.reduce(
    (s, v) => s + computeVideoPrice(v),
    0
  );
  const saving = sumIfBoughtIndividually - bundlePrice;

  if (saving > 0 && bundlePrice > 0) {
    return {
      type: 'bundle',
      bundlePrice,
      videoPrice,
      saving,
      unownedCount: unowned.length,
    };
  }
  return { type: 'single', price: videoPrice, saving: 0 };
}

// ================================================================
// C) Validation — اعتبارسنجی
// ================================================================
export function validateCourse(course) {
  const errors = [];
  if (!course) {
    errors.push('course is required');
    return errors;
  }
  if (!course.id) errors.push('course.id missing');
  if (!Array.isArray(course.videos)) errors.push('course.videos must be array');

  const ids = new Set();
  (course.videos || []).forEach((v, i) => {
    if (!v.id) errors.push(`video[${i}].id missing`);
    if (ids.has(v.id)) errors.push(`duplicate video.id: ${v.id}`);
    ids.add(v.id);
    const p = Number(v.price);
    if (Number.isNaN(p) || p < 0)
      errors.push(`video[${i}].price invalid: ${v.price}`);
  });

  const full = Number(course?.pricing?.fullCoursePrice);
  if (Number.isNaN(full) || full < 0)
    errors.push('pricing.fullCoursePrice invalid');

  return errors;
}

/**
 * بازسازی مشتق‌ها از تراکنش‌ها — منبع حقیقت
 */
export function recomputeDerived(transactions) {
  const unlockedSet = new Set();
  let totalPaid = 0;
  let totalRefunded = 0;

  for (const tx of transactions || []) {
    if (!tx || tx.status !== 'success') continue;
    if (tx.type === TX_TYPES.REFUND) {
      totalRefunded += Math.max(0, Number(tx.amount) || 0);
      (tx.videoIds || []).forEach((id) => unlockedSet.delete(id));
    } else {
      totalPaid += Math.max(0, Number(tx.amount) || 0);
      (tx.videoIds || []).forEach((id) => unlockedSet.add(id));
    }
  }

  return {
    unlockedVideoIds: Array.from(unlockedSet),
    totalPaid,
    totalRefunded,
  };
}

// ================================================================
// D) Utils
// ================================================================
export function toPersianDigits(n) {
  return String(n ?? '').replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
}

export function formatPrice(amount, currency = 'IRT') {
  const n = Number(amount) || 0;
  const formatted = toPersianDigits(n.toLocaleString('en-US'));
  const unit = currency === 'IRT' ? 'تومان' : currency;
  return `${formatted} ${unit}`;
}